/* Un documento del viaje en el formato genérico (párrafos, avisos, listas, casillas y tablas).
   Con `dia`, solo lo que menciona ese día (y de las tablas, solo sus filas). Las casillas se marcan
   de verdad: van a viajes-api como cualquier otro apunte. */
import { memo, type ReactNode } from 'react';
import { casillaHecha, marcarCasilla, useCasillaHecha } from '../datos/apuntes.ts';
import type { BloqueDoc, Documento as Doc, Seccion } from '../datos/tipos.ts';

interface Props {
  doc: Doc;
  dia?: number;                       // solo lo de ese día
  secciones?: string[];               // solo esas secciones (por id)
  titulos?: boolean;                  // títulos de sección (por defecto, sí)
}

const URL = /(https?:\/\/[^\s)]+)/g;
/** Texto con sus enlaces: los que trae el documento y las direcciones escritas a mano. */
function Texto({ texto, enlaces }: { texto: string; enlaces?: { texto: string; url: string }[] }) {
  if (enlaces?.length) {
    let resto = texto;
    const piezas: ReactNode[] = [];
    for (const [i, e] of enlaces.entries()) {
      const k = resto.indexOf(e.texto);
      if (k < 0) continue;
      piezas.push(resto.slice(0, k), <a key={i} href={e.url} target="_blank" rel="noopener">{e.texto} ↗</a>);
      resto = resto.slice(k + e.texto.length);
    }
    return <>{piezas}{resto}</>;
  }
  const partes = texto.split(URL);
  return <>{partes.map((p, i) => (i % 2 ? <a key={i} href={p} target="_blank" rel="noopener">{p.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')} ↗</a> : p))}</>;
}

export const Casilla = memo(function Casilla({ id, children }: { id: string; children: ReactNode }) {
  const hecha = useCasillaHecha(id);
  return (
    <label className={`casilla ${hecha ? 'is-hecha' : ''}`}>
      <input type="checkbox" checked={hecha} onChange={e => marcarCasilla(id, e.target.checked)} />
      <span className="casilla__marca" aria-hidden="true">{hecha ? '済' : ''}</span>
      <span className="casilla__texto">{children}</span>
    </label>
  );
});

function Tabla({ b, dia }: { b: Extract<BloqueDoc, { tipo: 'tabla' }>; dia?: number }) {
  const filas = b.filas.map((f, i) => ({ f, i })).filter(({ i }) => dia == null || !b.filasDias || b.filasDias[i]?.includes(dia));
  if (!filas.length) return null;
  const dos = b.cabecera.length === 2;
  return (
    <div className="doc-tabla">
      {filas.map(({ f, i }) => (
        <dl key={i} className={`fila ${dos ? 'fila--par' : ''}`}>
          <dt>{f[0]}</dt>
          {f.slice(1).map((c, j) => c && c !== '—' ? (
            <dd key={j}>
              {!dos && <span className="fila__clave">{b.cabecera[j + 1]}</span>}
              <Texto texto={c} enlaces={b.enlaces?.[i]?.[j + 1]} />
            </dd>
          ) : null)}
        </dl>
      ))}
    </div>
  );
}

function Bloque({ b, dia }: { b: BloqueDoc; dia?: number }) {
  switch (b.tipo) {
    case 'parrafo': return <p className="texto"><Texto texto={b.texto} enlaces={b.enlaces} /></p>;
    case 'lista': return <p className="texto doc-lista">· <Texto texto={b.texto} enlaces={b.enlaces} /></p>;
    case 'subtitulo': return <h4 className="doc-sub">{b.texto}</h4>;
    case 'aviso': return <div className="aviso">{b.titulo && <b>{b.titulo}</b>} <Texto texto={b.texto} /></div>;
    case 'casilla': return <Casilla id={b.id}><Texto texto={b.texto} /></Casilla>;
    case 'tabla': return <Tabla b={b} dia={dia} />;
  }
}

/** Los bloques de un documento que tocan un día (para la hoja del día). */
export const bloquesDelDia = (s: Seccion, dia: number) =>
  s.bloques.filter(b => b.dias?.includes(dia) || (b.tipo === 'tabla' && b.filasDias?.some(x => x.includes(dia))));

export function Documento({ doc, dia, secciones, titulos = true }: Props) {
  const lista = doc.secciones
    .filter(s => !secciones || secciones.includes(s.id))
    .map(s => ({ s, bloques: dia == null ? s.bloques : bloquesDelDia(s, dia) }))
    .filter(x => x.bloques.length);
  if (!lista.length) return null;
  return (
    <div className="doc">
      {lista.map(({ s, bloques }) => (
        <section key={s.id} className="doc-seccion">
          {titulos && s.titulo && <h3>{s.n ? `${s.n} · ` : ''}{s.titulo}</h3>}
          {bloques.map((b, i) => <Bloque key={i} b={b} dia={dia} />)}
        </section>
      ))}
    </div>
  );
}

/** Cuántas casillas hay y cuántas están hechas (para el progreso de «Antes de salir»). */
export function progresoCasillas(docs: Doc[]) {
  const ids = docs.flatMap(d => d.secciones.flatMap(s => s.bloques.filter(b => b.tipo === 'casilla').map(b => (b as { id: string }).id)));
  return { total: ids.length, hechas: ids.filter(casillaHecha).length };
}
